import { Close, Delete, DeleteOutline, MoreHoriz, Person, PhoneForwarded, TimerOffOutlined, TimerOutlined } from "@mui/icons-material";
import { Alert, Box, IconButton, Link, Tooltip } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { CHATYME_EMBED_HOST_MESSAGE_TYPE } from "../constants/chatymeEmbedHostMessageTypes";
import { ChatProcess, ChatProcessStatus, SocketChatSession, SocketType, UserRole } from "../types";
import { postChatEmbedHostMessage } from "../utils/chatEmbedHost";

const ACTIONS_AUTO_CLOSE_MS = 5000;

/** When true, session action icons are behind a ⋯ menu. When false/unset, icons stay always visible. */
const USE_COLLAPSED_SESSION_ACTIONS_MENU =
  process.env.REACT_APP_AGENT_SESSION_ACTIONS_COLLAPSED_MENU === 'true';

export type InactivityAutoCloseConfig = {
    enabled: boolean;
    disabled: boolean;
    onChange: (enabled: boolean) => void;
    label: string;
    tooltip: string;
    onLabel: string;
    offLabel: string;
};

type ChatProfileInfoProps = {
    isLoadingSession: boolean;
    isPendingSession: boolean;
    currentSessionRef: React.MutableRefObject<SocketChatSession | null>;
    completeProcess: ChatProcess;
    setChatCompleteProcess: (chatCompleteProcess: ChatProcess) => void;
    inactivityAutoClose: InactivityAutoCloseConfig;
    endSessionTooltip: string;
    closeSessionTooltip: string;
    transferToPhoneTooltip: string;
    sessionActionsMenuLabel: string;
}

const ChatProfileInfo: React.FC<ChatProfileInfoProps> = ({
    isLoadingSession,
    isPendingSession,
    currentSessionRef,
    completeProcess,
    setChatCompleteProcess,
    inactivityAutoClose,
    endSessionTooltip,
    closeSessionTooltip,
    transferToPhoneTooltip,
    sessionActionsMenuLabel
}) => {
    const [actionsOpen, setActionsOpen] = useState(false);
    const isHoveringMenuRef = useRef(false);
    const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const clearAutoCloseTimer = useCallback(() => {
        if (autoCloseTimerRef.current !== null) {
            clearTimeout(autoCloseTimerRef.current);
            autoCloseTimerRef.current = null;
        }
    }, []);

    const scheduleAutoClose = useCallback(() => {
        if (!USE_COLLAPSED_SESSION_ACTIONS_MENU) {
            return;
        }
        clearAutoCloseTimer();
        autoCloseTimerRef.current = setTimeout(() => {
            if (!isHoveringMenuRef.current) {
                setActionsOpen(false);
            }
        }, ACTIONS_AUTO_CLOSE_MS);
    }, [clearAutoCloseTimer]);

    useEffect(() => {
        if (!USE_COLLAPSED_SESSION_ACTIONS_MENU || !actionsOpen) {
            clearAutoCloseTimer();
            return;
        }

        if (!isHoveringMenuRef.current) {
            scheduleAutoClose();
        }

        return clearAutoCloseTimer;
    }, [actionsOpen, clearAutoCloseTimer, scheduleAutoClose]);

    useEffect(() => {
        setActionsOpen(false);
        isHoveringMenuRef.current = false;
        clearAutoCloseTimer();
    }, [isLoadingSession, isPendingSession, clearAutoCloseTimer]);

    if (!currentSessionRef.current || isLoadingSession || isPendingSession) {
        return null;
    }

    if (completeProcess.status === ChatProcessStatus.CONFIRM_BOX) {
        return null;
    }

    const actionsInteractive = !USE_COLLAPSED_SESSION_ACTIONS_MENU || actionsOpen;

    const handleMenuMouseEnter = () => {
        if (!USE_COLLAPSED_SESSION_ACTIONS_MENU) {
            return;
        }
        isHoveringMenuRef.current = true;
        clearAutoCloseTimer();
    };

    const handleMenuMouseLeave = () => {
        if (!USE_COLLAPSED_SESSION_ACTIONS_MENU) {
            return;
        }
        isHoveringMenuRef.current = false;
        if (actionsOpen) {
            scheduleAutoClose();
        }
    };

    const handleToggleActions = () => {
        setActionsOpen((prev) => {
            const next = !prev;
            if (next && !isHoveringMenuRef.current) {
                scheduleAutoClose();
            }
            return next;
        });
    };

    const handleTransferToPhone = () => {
        const session = currentSessionRef.current;
        if (!session) {
            return;
        }

        if (USE_COLLAPSED_SESSION_ACTIONS_MENU) {
            setActionsOpen(false);
        }
        postChatEmbedHostMessage({
            type: CHATYME_EMBED_HOST_MESSAGE_TYPE.CHAT_TRANSFER_TO_PHONE,
            sessionId: session.id,
            externalUserId: session.user?.external_user_id ?? null
        });
    };

    const inactivityTooltip = (
        <Box sx={{ maxWidth: 220 }}>
            <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>
                {inactivityAutoClose.label}: {inactivityAutoClose.enabled ? inactivityAutoClose.onLabel : inactivityAutoClose.offLabel}
            </Box>
            <Box component="span" sx={{ display: 'block', fontSize: '0.75rem', mt: 0.5, lineHeight: 1.3 }}>
                {inactivityAutoClose.tooltip}
            </Box>
        </Box>
    );

    const canEndSession = !currentSessionRef.current.assign?.end;
    const isCompletePending = completeProcess.status === ChatProcessStatus.PENDING;

    const actionItems = (
        <>
            <Tooltip title={transferToPhoneTooltip} arrow placement="bottom">
                <span className="chatyme-profile-actions-item">
                    <IconButton
                        className="chatyme-transfer-to-phone-btn"
                        color="primary"
                        size="small"
                        onClick={handleTransferToPhone}
                        disabled={!actionsInteractive}
                        tabIndex={actionsInteractive ? 0 : -1}
                        aria-label={transferToPhoneTooltip}
                    >
                        <PhoneForwarded fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>
            <Tooltip title={inactivityTooltip} arrow placement="bottom">
                <span className="chatyme-profile-actions-item">
                    <IconButton
                        className={`chatyme-inactivity-auto-close-btn${inactivityAutoClose.enabled ? ' chatyme-inactivity-auto-close-btn--on' : ' chatyme-inactivity-auto-close-btn--off'}`}
                        color={inactivityAutoClose.enabled ? 'warning' : 'default'}
                        size="small"
                        onClick={() => inactivityAutoClose.onChange(!inactivityAutoClose.enabled)}
                        disabled={inactivityAutoClose.disabled || !actionsInteractive}
                        tabIndex={actionsInteractive ? 0 : -1}
                        aria-label={inactivityAutoClose.label}
                        aria-pressed={inactivityAutoClose.enabled}
                    >
                        {inactivityAutoClose.enabled ? <TimerOutlined fontSize="small" /> : <TimerOffOutlined fontSize="small" />}
                    </IconButton>
                </span>
            </Tooltip>
            {canEndSession && (
                <Tooltip title={endSessionTooltip} arrow placement="bottom">
                    <span className="chatyme-profile-actions-item">
                        <IconButton
                            onClick={() => {
                                if (USE_COLLAPSED_SESSION_ACTIONS_MENU) {
                                    setActionsOpen(false);
                                }
                                setChatCompleteProcess({ title: 'End Session', socketType: SocketType.CHATENDED, status: ChatProcessStatus.CONFIRM_BOX });
                            }}
                            size="small"
                            disabled={isCompletePending || !actionsInteractive}
                            tabIndex={actionsInteractive ? 0 : -1}
                            aria-label={endSessionTooltip}
                        >
                            <DeleteOutline />
                        </IconButton>
                    </span>
                </Tooltip>
            )}
            <Tooltip title={closeSessionTooltip} arrow placement="bottom">
                <span className="chatyme-profile-actions-item">
                    <IconButton
                        onClick={() => {
                            if (USE_COLLAPSED_SESSION_ACTIONS_MENU) {
                                setActionsOpen(false);
                            }
                            setChatCompleteProcess({ title: 'Close Session', socketType: SocketType.CHATCLOSED, status: ChatProcessStatus.CONFIRM_BOX });
                        }}
                        size="small"
                        color="error"
                        disabled={isCompletePending || !actionsInteractive}
                        tabIndex={actionsInteractive ? 0 : -1}
                        aria-label={closeSessionTooltip}
                    >
                        <Delete />
                    </IconButton>
                </span>
            </Tooltip>
        </>
    );

    const actionButtons = USE_COLLAPSED_SESSION_ACTIONS_MENU ? (
        <Box
            className={`chatyme-profile-actions-menu${actionsOpen ? ' chatyme-profile-actions-menu--open' : ''}`}
            onMouseEnter={handleMenuMouseEnter}
            onMouseLeave={handleMenuMouseLeave}
        >
            <Box className="chatyme-profile-actions-tray" aria-hidden={!actionsOpen}>
                {actionItems}
            </Box>
            <Tooltip title={sessionActionsMenuLabel} arrow placement="bottom">
                <IconButton
                    className="chatyme-profile-actions-trigger"
                    size="small"
                    onClick={handleToggleActions}
                    aria-label={sessionActionsMenuLabel}
                    aria-expanded={actionsOpen}
                    aria-haspopup="true"
                >
                    {actionsOpen ? <Close fontSize="small" /> : <MoreHoriz fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>
    ) : (
        <Box className="chatyme-profile-actions-menu chatyme-profile-actions-menu--inline">
            <Box className="chatyme-profile-actions-tray">
                {actionItems}
            </Box>
        </Box>
    );

    const userIdInfo = () => {
        if (currentSessionRef.current && currentSessionRef.current.user.role === UserRole.Client && currentSessionRef.current.user.external_user_id) {
            return `${currentSessionRef.current.user.external_user_id} - `;
        }
        return '';
    };

    const clientName = `${userIdInfo()}${currentSessionRef.current.user.name} ${currentSessionRef.current.user.surname}`.trim();

    return (
        <Box className="chatyme-alert-box">
            <Alert
                className="chatyme-profile-info-alert"
                variant="outlined"
                severity="info"
                icon={<Person fontSize="small" />}
                sx={{
                    borderRadius: '0',
                    '& .MuiAlert-action': { paddingTop: '0', alignItems: 'center', flexShrink: 0, ml: 0.5, position: 'relative', zIndex: 2, overflow: 'visible' },
                    '& .MuiAlert-message': { minWidth: 0, flex: 1, overflow: 'hidden', position: 'relative', zIndex: 1 }
                }}
                action={actionButtons}
            >
                {currentSessionRef.current.config?.client_profile_url ? (
                    <Link
                        className="chatyme-profile-info-name"
                        href={currentSessionRef.current.config?.client_profile_url || ''}
                        underline="none"
                        target="_blank"
                        rel="noopener noreferrer"
                        title={clientName}
                    >
                        {clientName}
                    </Link>
                ) : (
                    <Box className="chatyme-profile-info-name" component="span" title={clientName}>
                        {clientName}
                    </Box>
                )}
            </Alert>
        </Box>
    );
};

export default ChatProfileInfo;
